import type { Workflow, WorkflowAnyDefinition, WorkflowNodeTrace, WorkflowSampleResponse,
  workflowRunTraceSchema } from "@mlx/contracts";
import type { z } from "zod";
import type { WorkflowProcessState } from "./workflow-process-state";
import { workflowItemHref } from "./workflow-item-links";

export type RunTrace = z.infer<typeof workflowRunTraceSchema>;
/**
 * One run as the canvas shows it: a durable item attempt or a draft sample.
 * Both carry the same retained step traces, so the graph, rail and banner
 * read one model whichever executor produced it.
 */
export type RunView = {
  key: string;
  kind: "item" | "sample";
  label: string;
  status: "pending" | "running" | "succeeded" | "failed" | "cancelled" | "retry";
  /** The attempt can still progress; a running step of a dead attempt is interrupted. */
  live: boolean;
  version: number;
  definition: WorkflowAnyDefinition;
  nodes: WorkflowNodeTrace[];
  error: string | null;
  attempt?: number;
  latestAttempt?: number;
  recordHref?: string;
  traceLocation?: { runId: string; itemId: string; attempt: number };
  execution?: RunTrace["execution"];
  process?: WorkflowProcessState & { reviewBatchId?: string | null };
};
export type NodeStatus = WorkflowNodeTrace["status"] | "waiting" | "not_run" | "unavailable" | "interrupted" | "idle";
export const statusLabel: Record<NodeStatus, string> = {
  running: "Running", succeeded: "Completed", failed: "Failed", waiting: "Waiting",
  not_run: "Not run", unavailable: "No step trace", interrupted: "Interrupted", idle: "",
};

export function runFromTrace(trace: RunTrace): RunView {
  return { key: `${trace.runId}:${trace.itemId}:${trace.attempt}`, kind: "item", label: trace.title ?? trace.itemId,
    recordHref: workflowItemHref(trace), process: trace,
    traceLocation: { runId: trace.runId, itemId: trace.itemId, attempt: trace.attempt }, execution: trace.execution,
    status: trace.itemStatus, live: trace.itemStatus === "running" && trace.attempt === trace.latestAttempt,
    version: trace.workflowVersion, definition: trace.definition, nodes: trace.nodes, error: trace.error,
    attempt: trace.attempt, latestAttempt: trace.latestAttempt };
}

/**
 * Samples only overlay the version they ran; an older sample's steps may no
 * longer exist. `label` names how the run started: a Test sample, a scheduled
 * run or the authoring agent's own test.
 */
export function runFromSample(sample: WorkflowSampleResponse, workflow: Workflow, label = "Sample"): RunView | null {
  if (sample.workflowId !== workflow.id || sample.version !== workflow.version) return null;
  const started = sample.nodes.length > 0;
  const status = sample.status === "queued" || (sample.status === "running" && !started) ? "pending" : sample.status;
  return { key: sample.runId, kind: "sample", label, status,
    live: sample.status === "queued" || sample.status === "running", version: sample.version,
    definition: workflow.definition, nodes: sample.nodes, error: sample.error ?? sample.preview?.error ?? null };
}

export function nodeStatus(id: string, run: RunView | null | undefined): NodeStatus {
  if (!run) return "idle";
  const node = run.nodes.find(n => n.nodeId === id);
  if (node) return node.status === "running" && !run.live ? "interrupted" : node.status;
  if (!run.nodes.length) return run.status === "pending" ? "waiting" : "unavailable";
  return run.live ? "waiting" : "not_run";
}

/** A dependency fired once its producer completed and its consumer started. */
export function edgeState(source: string, target: string, run?: RunView | null) {
  const consumed = nodeStatus(source, run) === "succeeded" &&
    Boolean(run?.nodes.some(node => node.nodeId === target));
  return { consumed, animated: consumed && nodeStatus(target, run) === "running" };
}

export function traceDefinition(workflow: Workflow, run?: RunView | null) {
  return run?.definition ?? workflow.definition;
}

export function nodeDuration(trace: WorkflowNodeTrace | undefined): number | undefined {
  return trace?.completedAt ? trace.completedAt - trace.startedAt : undefined;
}

export type RunProgress = { done: number; total: number; startedAt?: number; completedAt?: number;
  failed?: { nodeId: string; label: string; error: string } };
export function runProgress(run: RunView): RunProgress {
  const starts = run.nodes.map(n => n.startedAt), ends = run.nodes.flatMap(n => n.completedAt ? [n.completedAt] : []);
  const failed = run.nodes.find(n => n.status === "failed");
  return {
    done: run.nodes.filter(n => n.status === "succeeded").length,
    total: run.definition.nodes.length,
    startedAt: starts.length ? Math.min(...starts) : undefined,
    completedAt: !run.live && ends.length ? Math.max(...ends) : undefined,
    failed: failed ? { nodeId: failed.nodeId, error: failed.error ?? "Step failed",
      label: run.definition.nodes.find(n => n.id === failed.nodeId)?.label ?? failed.nodeId } : undefined,
  };
}
